import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, API, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  ArrowLeft, Upload, FileSpreadsheet, Trash2, Download, Send, AlertCircle, CheckCircle2,
  MessageCircle, Copy, Truck, ChevronDown, ArrowUp,
} from "lucide-react";
import { toast } from "sonner";
import { buildWhatsAppUrl } from "../lib/phone";

const ACCEPT = ".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";
const UNITS = ["pcs", "kg", "ltr", "box", "bag", "g", "ml", "dozen", "carton"];

export default function BulkImport() {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [items, setItems] = useState([]); // each: {product, quantity, unit, supplier_ids: []}
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiClient.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    apiClient.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post("/import/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParsed(data);
      setItems(data.items.map((it) => ({ ...it, supplier_ids: [] })));
      toast.success(`Imported ${data.items.length} rows`);
      if (data.warnings?.length) toast.warning(`${data.warnings.length} row(s) skipped`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not parse file");
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadTemplateCsv = () => window.open(`${API}/import/template`, "_blank");
  const downloadTemplateXlsx = () => window.open(`${API}/import/template.xlsx`, "_blank");

  const updateItem = (idx, key, value) => {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
  };
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const toggleItemSupplier = (idx, sid) => {
    setItems((arr) =>
      arr.map((it, i) => {
        if (i !== idx) return it;
        const has = it.supplier_ids.includes(sid);
        return { ...it, supplier_ids: has ? it.supplier_ids.filter((x) => x !== sid) : [...it.supplier_ids, sid] };
      })
    );
  };

  const copyFromAbove = (idx) => {
    if (idx === 0) return;
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, supplier_ids: [...arr[idx - 1].supplier_ids] } : it)));
    toast.success("Copied from row above");
  };

  // Union of all per-item supplier picks
  const allPickedIds = Array.from(new Set(items.flatMap((it) => it.supplier_ids)));

  const submit = async () => {
    if (items.length === 0) { toast.error("No items"); return; }
    if (allPickedIds.length === 0) { toast.error("Pick supplier(s) for each item"); return; }
    for (const it of items) {
      if (!it.product?.trim()) { toast.error("All items need a product"); return; }
      const q = Number(it.quantity);
      if (!q || q <= 0) { toast.error("Quantity must be > 0"); return; }
      if (it.supplier_ids.length === 0) { toast.error(`Pick supplier for: ${it.product}`); return; }
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((it) => ({
          product: it.product.trim(),
          quantity: Number(it.quantity),
          unit: (it.unit || "pcs").trim(),
        })),
        supplier_ids: allPickedIds,
      };
      const { data } = await apiClient.post("/bulk-price-requests", payload);
      navigate(`/bulk-compare/${data.id}${customerId ? `?customer_id=${customerId}` : ""}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <AppHeader title="Bulk Import" subtitle="Excel / CSV → per-item supplier + WhatsApp" />
      <div className="px-4 pt-4 pb-28 space-y-4">
        <button onClick={() => navigate(-1)} data-testid="bulk-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {!parsed && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center">
                <FileSpreadsheet className="w-6 h-6" strokeWidth={2.5} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Upload your sheet</h3>
                <p className="text-xs text-slate-500">Blank rows and any of <b>PRODUCT/ITEM</b>, <b>NO OF PCS/QTY</b>, <b>UNIT</b> — all auto-detected</p>
              </div>
            </div>

            <input ref={fileRef} type="file" accept={ACCEPT} onChange={onFile} className="hidden" data-testid="bulk-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={parsing} data-testid="bulk-pick-file"
              className="w-full h-16 rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50/50 hover:bg-emerald-100/60 text-emerald-700 font-semibold gap-2 text-base">
              <Upload className="w-5 h-5" /> {parsing ? "Reading..." : "Choose .xlsx or .csv file"}
            </Button>

            <div className="grid grid-cols-2 gap-2">
              <Button onClick={downloadTemplateXlsx} variant="outline" data-testid="bulk-template-xlsx-btn"
                className="h-12 rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-2">
                <Download className="w-4 h-4" /> Excel template
              </Button>
              <Button onClick={downloadTemplateCsv} variant="ghost" data-testid="bulk-template-btn"
                className="h-12 rounded-xl text-slate-700 gap-2">
                <Download className="w-4 h-4" /> CSV template
              </Button>
            </div>
          </div>
        )}

        {parsed && (
          <>
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3 flex items-start gap-2" data-testid="bulk-parsed-banner">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-bold text-emerald-900">{items.length} rows imported</div>
                <div className="text-xs text-emerald-800">Assign supplier per row. Use "Same as above" for repeats.</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => { setParsed(null); setItems([]); }} data-testid="bulk-reupload"
                className="ml-auto text-emerald-700 hover:bg-emerald-100 rounded-lg">Re-upload</Button>
            </div>

            {parsed.warnings?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-xs text-amber-800 space-y-0.5" data-testid="bulk-warnings">
                <div className="flex items-center gap-1 font-bold mb-1"><AlertCircle className="w-3.5 h-3.5" /> {parsed.warnings.length} skipped</div>
                {parsed.warnings.slice(0, 4).map((w, i) => <div key={i}>• {w}</div>)}
                {parsed.warnings.length > 4 && <div>• ...and {parsed.warnings.length - 4} more</div>}
              </div>
            )}

            <div className="space-y-3" data-testid="bulk-items-list">
              {items.map((it, idx) => (
                <ItemCard
                  key={idx}
                  idx={idx}
                  item={it}
                  suppliers={suppliers}
                  canCopyAbove={idx > 0}
                  onChange={(k, v) => updateItem(idx, k, v)}
                  onRemove={() => removeItem(idx)}
                  onToggleSupplier={(sid) => toggleItemSupplier(idx, sid)}
                  onCopyAbove={() => copyFromAbove(idx)}
                />
              ))}
              {items.length === 0 && <p className="text-sm text-slate-500 text-center py-4">All rows removed. Re-upload to continue.</p>}
            </div>

            {customers.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-2">
                <Label>Link to customer (optional)</Label>
                <Select value={customerId || "none"} onValueChange={(v) => setCustomerId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="bulk-customer-select" className="h-12 rounded-xl"><SelectValue placeholder="No customer" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No customer</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.phone})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {suppliers.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                No suppliers yet — <a className="font-semibold underline" href="/suppliers/new">add one</a> before you can pick them per row.
              </div>
            )}

            <Button onClick={submit} disabled={submitting || items.length === 0 || allPickedIds.length === 0}
              data-testid="bulk-get-quotes-btn"
              className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-lg font-semibold gap-2">
              <Send className="w-5 h-5" /> {submitting ? "Getting quotes..." : `Get Quotes (${items.length} items · ${allPickedIds.length} suppliers)`}
            </Button>

            <div className="relative flex items-center gap-2 my-1">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">or</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <WhatsAppEnquiryPanel items={items} suppliers={suppliers} />
          </>
        )}

        {parsing && !parsed && (
          <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>
        )}
      </div>
    </>
  );
}

function ItemCard({ idx, item, suppliers, canCopyAbove, onChange, onRemove, onToggleSupplier, onCopyAbove }) {
  const picked = suppliers.filter((s) => item.supplier_ids.includes(s.id));
  return (
    <div data-testid={`bulk-item-${idx}`} className="bg-white rounded-2xl border border-slate-200 p-3 space-y-2">
      <div className="grid grid-cols-12 gap-2 items-center">
        <div className="col-span-1 text-center text-xs font-bold text-slate-400">{idx + 1}</div>
        <Input value={item.product} onChange={(e) => onChange("product", e.target.value)}
          placeholder="Product" className="col-span-6 h-11 rounded-lg" data-testid={`bulk-item-product-${idx}`} />
        <Input value={item.quantity} onChange={(e) => onChange("quantity", e.target.value)}
          type="number" min="0" step="0.01" placeholder="Qty" className="col-span-2 h-11 rounded-lg" data-testid={`bulk-item-qty-${idx}`} />
        <Select value={item.unit || "pcs"} onValueChange={(v) => onChange("unit", v)}>
          <SelectTrigger className="col-span-2 h-11 rounded-lg" data-testid={`bulk-item-unit-${idx}`}><SelectValue /></SelectTrigger>
          <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
        </Select>
        <button onClick={onRemove} data-testid={`bulk-item-remove-${idx}`}
          className="col-span-1 h-11 grid place-items-center text-slate-400 hover:text-red-600">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 pl-6">
        <SupplierPicker suppliers={suppliers} picked={item.supplier_ids} onToggle={onToggleSupplier} idx={idx} />
        {canCopyAbove && (
          <button onClick={onCopyAbove} data-testid={`bulk-item-copy-above-${idx}`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1.5 rounded-lg">
            <ArrowUp className="w-3 h-3" /> Same as above
          </button>
        )}
        {picked.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {picked.map((s) => (
              <span key={s.id} data-testid={`bulk-item-chip-${idx}-${s.id}`}
                className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                {s.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SupplierPicker({ suppliers, picked, onToggle, idx }) {
  const [open, setOpen] = useState(false);
  const count = picked.length;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`bulk-item-supplier-btn-${idx}`}
          className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border ${
            count > 0 ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"
          }`}>
          <Truck className="w-3 h-3" />
          {count > 0 ? `${count} supplier${count > 1 ? "s" : ""}` : "Pick supplier"}
          <ChevronDown className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2 max-h-72 overflow-y-auto">
        {suppliers.length === 0 ? (
          <p className="text-xs text-slate-500 p-2">Add a supplier first</p>
        ) : (
          suppliers.map((s) => (
            <label key={s.id} data-testid={`bulk-item-supplier-opt-${idx}-${s.id}`}
              className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-slate-50 cursor-pointer">
              <Checkbox checked={picked.includes(s.id)} onCheckedChange={() => onToggle(s.id)} />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{s.name}</div>
                <div className="text-[11px] text-slate-500">{s.phone}</div>
              </div>
            </label>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
}

function buildMessage(items, supplierName = "") {
  const lines = items.map((it, i) => `${i + 1}. ${it.product} — ${it.quantity} ${it.unit || "pcs"}`);
  const greet = supplierName ? `Namaste ${supplierName} 🙏` : "Namaste 🙏";
  return `${greet}\n\nPlease share your best rate for the following items:\n\n${lines.join("\n")}\n\nKindly reply with rates ASAP.`;
}

function WhatsAppEnquiryPanel({ items, suppliers }) {
  const [open, setOpen] = useState(false);

  // Group items by supplier_id
  const bySupplier = React.useMemo(() => {
    const map = new Map();
    for (const it of items) {
      for (const sid of it.supplier_ids || []) {
        if (!map.has(sid)) map.set(sid, []);
        map.get(sid).push(it);
      }
    }
    return map;
  }, [items]);

  const supplierGroups = suppliers
    .filter((s) => bySupplier.has(s.id))
    .map((s) => ({ supplier: s, items: bySupplier.get(s.id) }));

  const openWhatsApp = (phone, msg) => {
    const url = buildWhatsAppUrl(phone, msg);
    if (!url) { toast.error("This business's phone number looks invalid"); return; }
    window.open(url, "_blank");
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Message copied"); }
    catch { toast.error("Copy failed"); }
  };

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)} disabled={supplierGroups.length === 0} data-testid="whatsapp-enquiry-btn"
        variant="outline"
        className="w-full h-14 rounded-xl border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-lg font-semibold gap-2">
        <MessageCircle className="w-5 h-5" /> Send Bulk Enquiry via WhatsApp ({supplierGroups.length} suppliers)
      </Button>
    );
  }

  return (
    <div className="bg-white rounded-2xl border-2 border-emerald-300 p-4 space-y-3" data-testid="whatsapp-panel">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-emerald-700" /> WhatsApp enquiries
        </h4>
        <button onClick={() => setOpen(false)} className="text-xs text-slate-500 underline">Close</button>
      </div>
      <p className="text-xs text-slate-500">Each supplier gets a personalised list of only the items you assigned to them.</p>

      {supplierGroups.length === 0 && (
        <p className="text-sm text-slate-500 py-4 text-center">Pick supplier(s) on each row above first.</p>
      )}

      {supplierGroups.map(({ supplier, items: sItems }) => {
        const msg = buildMessage(sItems, supplier.name);
        return (
          <div key={supplier.id} data-testid={`wa-group-${supplier.id}`}
            className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-bold text-slate-900 truncate">{supplier.name}</div>
                <div className="text-xs text-slate-500">{supplier.phone || "no phone"} · {sItems.length} item{sItems.length > 1 ? "s" : ""}</div>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-2 text-[11px] font-mono whitespace-pre-wrap text-slate-700 max-h-24 overflow-y-auto">
              {msg}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" variant="outline" onClick={() => copyText(msg)} data-testid={`wa-copy-${supplier.id}`}
                className="h-10 rounded-lg gap-1">
                <Copy className="w-3.5 h-3.5" /> Copy
              </Button>
              <Button size="sm" onClick={() => openWhatsApp(supplier.phone, msg)} data-testid={`wa-send-${supplier.id}`}
                className="h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white gap-1">
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
